export interface Vec2 {
  x: number;
  y: number;
}

export const TAU = Math.PI * 2;

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * Frame-rate-independent damping. `stiffness` is in "1/seconds": at stiffness=k
 * after dt seconds we close (1 - e^{-k*dt}) of the remaining distance.
 * Equivalent to an exponential-decay critical-damped follow.
 */
export const damp = (current: number, target: number, stiffness: number, dt: number): number =>
  lerp(current, target, 1 - Math.exp(-stiffness * dt));

export const dampVec = (
  out: Vec2,
  current: Vec2,
  target: Vec2,
  stiffness: number,
  dt: number,
): Vec2 => {
  const t = 1 - Math.exp(-stiffness * dt);
  out.x = current.x + (target.x - current.x) * t;
  out.y = current.y + (target.y - current.y) * t;
  return out;
};

export const length = (x: number, y: number): number => Math.hypot(x, y);

export const normalize = (x: number, y: number): Vec2 => {
  const l = Math.hypot(x, y);
  if (l < 1e-6) return { x: 0, y: 0 };
  return { x: x / l, y: y / l };
};

/** Shortest signed angle from `a` to `b`, in radians, in [-PI, PI]. */
export const angleDelta = (a: number, b: number): number => {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  else if (d < -Math.PI) d += TAU;
  return d;
};
